// The share token in a public page's URL (`?share_token=…`, ADR-0019): reading
// it the way the source reads it, and keeping it out of anything that leaves
// the browser.
//
// In `lib/` beside `route-paths.ts`, which builds the URLs the token rides:
// pure string work, no `window`, no `node:*`, so either graph may call it — the
// public header and the resolver read the token, the PostHog sink (#116) will
// strip it, and the public share routes' `head` keeps it out of a `Referer`.

const SHARE_TOKEN_PARAM = "share_token";

/**
 * The page's share token as the source reads it — `URLSearchParams.get`
 * (`route-utils.ts:52-56`): percent-decoded, the first of a repeat — or `null`
 * when it is absent or empty, which the source treats as "no token"
 * (`ShareButton.tsx:77-79`).
 */
export function readShareToken(search: string): string | null {
  const query = search.startsWith("?") ? search.slice(1) : search;
  return new URLSearchParams(query).get(SHARE_TOKEN_PARAM) || null;
}

/**
 * `url` with every `share_token` parameter removed — the rule ADR-0019
 * decision 12 sets for `$current_url` and `$pathname` on the public share
 * routes, and the one the PostHog page lifecycle (#116) must apply before a
 * page view leaves the browser. Modsy sent the live token there on every view
 * (research §3.8).
 *
 * Works on an absolute URL or a path with a query. Every other parameter keeps
 * its place **and its own spelling** — the query is split by hand rather than
 * re-serialised through `URLSearchParams`, which would rewrite `%20` as `+` and
 * change a string the dashboards group by. The hash is kept.
 */
export function stripShareToken(url: string): string {
  const hashAt = url.indexOf("#");
  const hash = hashAt === -1 ? "" : url.slice(hashAt);
  const beforeHash = hashAt === -1 ? url : url.slice(0, hashAt);
  const queryAt = beforeHash.indexOf("?");
  if (queryAt === -1) return url;

  const kept = beforeHash
    .slice(queryAt + 1)
    .split("&")
    .filter((pair) => !isShareTokenPair(pair));
  const query = kept.length === 0 ? "" : `?${kept.join("&")}`;
  return `${beforeHash.slice(0, queryAt)}${query}${hash}`;
}

function isShareTokenPair(pair: string): boolean {
  const key = pair.split("=", 1)[0] ?? "";
  try {
    return decodeURIComponent(key.replaceAll("+", " ")) === SHARE_TOKEN_PARAM;
  } catch {
    // A malformed escape in a key names no parameter `URLSearchParams` would
    // read as the token either.
    return false;
  }
}

/**
 * What every public share route's `head` carries (ADR-0019 decision 12):
 *
 *   - `noindex`, as the public product page's does — the source's sits in its
 *     `index.html`, app-wide.
 *   - `referrer: strict-origin`, so a `Referer` carries this origin alone,
 *     never the path or the `?share_token=` — to a manufacturer's site, and to
 *     this BFF's own server functions. Cross-origin that is what the browser's
 *     default already sends, so it changes nothing a visitor sees. Not
 *     `no-referrer`: under it a same-origin `POST` carries `Origin: null`, which
 *     the CSRF middleware (`start.ts`) refuses — every server function on the
 *     page would fail.
 *
 * No title: the source's tab title is static app-wide (`index.html:5`).
 */
export function publicShareHead() {
  return {
    meta: [
      { name: "robots", content: "noindex" },
      { name: "referrer", content: "strict-origin" },
    ],
  };
}
